import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import Loader from 'components/Loader';
import React from 'react';

import { SlideSelectionTable, SlideSelectionTableRow } from './SlideSelectionTable';

interface CaseManifestSourcePanelProps {
  canIncludeAssigned: boolean;
  canReadSource: boolean;
  description: string;
  disabled: boolean;
  hasAssignedError: boolean;
  hasPendingError: boolean;
  includeAssigned: boolean;
  isLoading: boolean;
  loadingText: string;
  onIncludeAssignedChange: (includeAssigned: boolean) => void;
  onRetry: () => void;
  onSelectedSlideIdsChange: (slideIds: string[]) => void;
  rows: SlideSelectionTableRow[];
  /** Human-readable scope for empty-state copy, e.g. "this study" or "this lab". */
  scopeName: string;
  selectedSlideIds: string[];
  selectionMessage: string | null;
  selectionScopeKey: string;
  sourceName: string;
  sourceUnavailable: boolean;
}

export const CaseManifestSourcePanel = ({
  canIncludeAssigned,
  canReadSource,
  description,
  disabled,
  hasAssignedError,
  hasPendingError,
  includeAssigned,
  isLoading,
  loadingText,
  onIncludeAssignedChange,
  onRetry,
  onSelectedSlideIdsChange,
  rows,
  scopeName,
  selectedSlideIds,
  selectionMessage,
  selectionScopeKey,
  sourceName,
  sourceUnavailable,
}: CaseManifestSourcePanelProps) => {
  const hasError = includeAssigned ? hasAssignedError : hasPendingError;
  const isEmpty = !isLoading && !sourceUnavailable && !hasError && rows.length === 0;
  return (
    <Box my={1}>
      {description && !isEmpty ? (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontSize: 16 }}>
          {description}
        </Typography>
      ) : null}
      {canIncludeAssigned && canReadSource && (
        <FormControlLabel
          control={
            <Checkbox
              checked={includeAssigned}
              disabled={disabled}
              onChange={(_event, checked) => onIncludeAssignedChange(checked)}
            />
          }
          label="Include assigned slides"
        />
      )}
      {selectionMessage && <Alert severity="warning">{selectionMessage}</Alert>}
      {!includeAssigned && hasPendingError && (
        <Alert severity="error">Could not load {sourceName}. Your case assignment is unchanged.</Alert>
      )}
      {includeAssigned && hasAssignedError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Could not load slide assignments. Your case assignment is unchanged. Retry or return to {sourceName}.
        </Alert>
      )}
      {isEmpty && !includeAssigned && (
        <Alert severity="info">
          No slides in {scopeName} are awaiting case assignment.
          {canIncludeAssigned ? ' Turn on "Include assigned slides" to find and move an already-assigned slide.' : ''}
        </Alert>
      )}
      {isEmpty && includeAssigned && <Alert severity="info">No slides are available in {scopeName}.</Alert>}
      {isLoading && (
        <Box mb={1.5}>
          <Loader inline message={loadingText} testId="pending-slides-loading-indicator" />
        </Box>
      )}
      {!isEmpty && (
        <SlideSelectionTable
          isLoading={isLoading || sourceUnavailable}
          onSelectedSlideIdsChange={onSelectedSlideIdsChange}
          selectionScopeKey={selectionScopeKey}
          rows={rows}
          selectedSlideIds={selectedSlideIds}
        />
      )}
    </Box>
  );
};
